
import { useState } from "react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { saveTasks, loadTasks, updateFrequentTasks } from "@/utils/storage";
import { useToast } from "@/hooks/use-toast";
import { Task } from "@/types/task";
import { Bell, Volume2 } from 'lucide-react';

interface TaskInputFormProps {
  onClose: () => void;
}

export default function TaskInputForm({ onClose }: TaskInputFormProps) {
  const [taskTitle, setTaskTitle] = useState("");
  const [scheduledTime, setScheduledTime] = useState("");
  const [duration, setDuration] = useState<number>(15);
  const [reminderSet, setReminderSet] = useState(true); // Default to true
  const [completionSound, setCompletionSound] = useState(true);
  const { toast } = useToast();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!taskTitle.trim()) {
      toast({
        title: "Task Required",
        description: "Please enter a task description",
        variant: "destructive",
      });
      return;
    }
    
    if (!scheduledTime) {
      toast({
        title: "Time Required",
        description: "Please enter a scheduled time",
        variant: "destructive",
      });
      return;
    }

    try {
      // Load existing tasks
      const existingTasks = await loadTasks();
      console.log("Existing tasks before adding new one:", existingTasks.length);
      
      // Create new task
      const newTask: Task = {
        id: crypto.randomUUID(),
        title: taskTitle,
        duration: duration,
        scheduledTime: scheduledTime,
        completed: false,
        reminderSet: reminderSet,
        completionSound: completionSound,
        createdAt: new Date().toISOString()
      };
      
      // Track for frequently used tasks
      updateFrequentTasks(newTask);
      
      // Add new task to existing tasks
      const updatedTasks = [...existingTasks, newTask];
      
      // Save updated tasks
      await saveTasks(updatedTasks);
      
      toast({
        title: "Task Added",
        description: `"${taskTitle}" added for ${scheduledTime}`,
      });
      
      if (reminderSet) {
        toast({
          title: "Reminder Set",
          description: `You'll be reminded when it's time for "${taskTitle}"`,
        });
        
        if ('Notification' in window && Notification.permission !== 'granted' && Notification.permission !== 'denied') {
          Notification.requestPermission();
        }
      }
      
      // Dispatch a custom event to notify other components
      console.log("Dispatching tasks-updated event with", updatedTasks.length, "tasks");
      const tasksUpdatedEvent = new CustomEvent('tasks-updated', { 
        detail: { tasks: updatedTasks }
      });
      window.dispatchEvent(tasksUpdatedEvent);
      
      // Reset form and close dialog
      setTaskTitle("");
      setScheduledTime("");
      setDuration(15);
      onClose();
      
    } catch (error) {
      console.error("Error adding task:", error);
      toast({
        title: "Error",
        description: "Failed to save task. Please try again.",
        variant: "destructive",
      });
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4 mt-4">
      <div className="space-y-2">
        <label htmlFor="task-title" className="block text-sm font-medium">
          Task
        </label>
        <Input
          id="task-title"
          value={taskTitle}
          onChange={(e) => setTaskTitle(e.target.value)}
          placeholder="What do you need to do?"
          className="w-full"
        />
      </div>
      
      <div className="space-y-2">
        <label htmlFor="scheduled-time" className="block text-sm font-medium">
          Start Time
        </label>
        <Input
          id="scheduled-time"
          type="time"
          value={scheduledTime}
          onChange={(e) => setScheduledTime(e.target.value)}
          className="w-full"
        />
      </div>
      
      <div className="space-y-2">
        <label htmlFor="duration" className="block text-sm font-medium">
          Duration (minutes)
        </label>
        <Input
          id="duration"
          type="number"
          min="1"
          max="480"
          value={duration}
          onChange={(e) => setDuration(Number(e.target.value))}
          className="w-full"
        />
      </div>
      
      <div className="space-y-3 p-3 border rounded-lg bg-white/70">
        <div className="flex items-center space-x-2">
          <Checkbox 
            id="reminder" 
            checked={reminderSet}
            onCheckedChange={(checked) => setReminderSet(checked === true)}
          />
          <Label htmlFor="reminder" className="flex items-center gap-2">
            <Bell className="h-4 w-4 text-muted-foreground" />
            Set reminder for this task
          </Label>
        </div>
        
        <div className="flex items-center space-x-2">
          <Checkbox 
            id="completionSound" 
            checked={completionSound}
            onCheckedChange={(checked) => setCompletionSound(checked === true)}
          />
          <Label htmlFor="completionSound" className="flex items-center gap-2">
            <Volume2 className="h-4 w-4 text-muted-foreground" />
            Play sound on completion
          </Label>
        </div>
      </div>
      
      <div className="flex justify-end space-x-2 pt-4">
        <Button 
          type="button" 
          variant="outline" 
          onClick={onClose}
        >
          Cancel
        </Button>
        <Button type="submit">Add Task</Button>
      </div>
    </form>
  );
}
